import type { ActionTree, GetterTree, MutationTree } from "vuex";

import { keycloak } from "@/keycloak/Keycloak";
import router, { routerRightCheck } from "@/router";
import type { LisaButtonTarget } from "@/graphql";
import { normalizeLisaButtonTarget } from "@/functions/lisaButtonNavigation";
import { cloneDeep } from "lodash";

export type GalleryImage = {
  itemImageSrc: string;
  thumbnailImageSrc?: string;
  alt?: string;
  title?: string;
  type?: "video" | "audio" | "image";
};

const localState = {
  user: null as null | any, // TODO: typing
  preferredSite: null as null | number,
  preferredBoard: null,
  lastPage: null,
  isLogin: false,
  needSetup: {
    state: false,
  },
};

export const getters: GetterTree<typeof localState, any> = {
  preferredName: (state) => state.user?.preferredName,
  lastPage: (state) => state.lastPage || state.user?.initialPage,
  lisaButtonTarget: (state) => normalizeLisaButtonTarget(state.user?.lisaButtonTarget),
  username: (state) => state.user?.username,
  userId: (state) => state.user?.id,
  user: (state) => state.user,
  initialPage: (state) => {
    const page = state.user?.initialPage;

    if (!page) {
      return "/dashboard";
    }

    const route = router.resolve(page);

    return routerRightCheck(route.meta) ? page : "/dashboard";
  },
  profilePictureId: (state) => state.user?.pictureId,
  site: (state) => state.user?.site,
  isAnonym: (state) => state.user?.anonym,
  preferredSite: (state) => state.preferredSite,
  preferredBoard: (state) => state.preferredBoard,
  department: (state) => state.user?.team,
  hasAccessRight: (state) => (right: string) => state.user?.accessRights?.includes(right),
  isLogin: (state) => state.isLogin,
  showDarkTheme: (state) => state.user?.darkMode,
  getModifier: (state) => state.user?.modifier,
  getLocale: (state) => state.user?.language,
  getDepartmentId: (state) => state.user?.teamId,
  needSetup: (state) => state.needSetup ?? { state: false, token: "" },
};

const actions: ActionTree<typeof localState, any> = {
  updateLastPage(context, payload) {
    context.commit("updateLastPage", payload);
  },
  updateLisaButtonTarget(context, payload: LisaButtonTarget) {
    context.commit("updateLisaButtonTarget", payload);
  },
  updateStartUpBoard(context, payload: number | null) {
    context.commit("updateStartUpBoard", payload);
  },
  updatePreferredSite(context, payload) {
    context.commit("updateSite", payload);
  },
  updatePreferredBoard(context, payload) {
    context.commit("updateBoard", payload);
  },
  updateJWT(context, payload) {
    context.commit("update", payload);
  },
  setUser(context, payload) {
    context.commit("set", payload);
  },
  setUserPicture(context, payload) {
    context.commit("setUserPicture", payload);
  },
  setAccessRights(context, payload) {
    context.commit("setAccessRights", payload);
  },
  async recoverSession(context) {
    const [{ apolloClient }, { MeDocument }] = await Promise.all([
      import("@/graphql/apollo"),
      import("@/graphql"),
    ]);
    try {
      const { data } = await apolloClient.query({
        query: MeDocument,
        fetchPolicy: "network-only",
      });
      if (data.me) {
        const { Department, Picture, ...user } = cloneDeep(data.me);
        const currentUser = {
          ...user,
          team: Department,
          pictureId: Picture?.fileUUID,
        };
        context.commit("set", currentUser);
        return currentUser;
      }
    } catch (error) {
      const graphQLErrors =
        typeof error === "object" && error != null && "graphQLErrors" in error
          ? error.graphQLErrors
          : undefined;
      if (
        Array.isArray(graphQLErrors) &&
        graphQLErrors.some(
          (graphQLError) =>
            typeof graphQLError === "object" &&
            graphQLError != null &&
            "message" in graphQLError &&
            graphQLError.message === "VALID_LOGIN_NO_LISA_USER",
        )
      ) {
        context.commit("setSetup", { state: true });
        return false;
      }
      throw error;
    }
    return false;
  },
  async logout(context) {
    context.commit("set", null);
    const logoutUrl = new URL(keycloak.createLogoutUrl());
    logoutUrl.searchParams.set("post_logout_redirect_uri", window.location.origin);
    window.location.replace(logoutUrl.toString());
  },
  async setSetup(context, payload) {
    context.commit("setSetup", payload);
  },
};

const mutations: MutationTree<typeof localState> = {
  setSetup(state, payload) {
    state.needSetup = payload;
  },
  setUserPicture(state, payload) {
    if (state.user != null) {
      state.user.pictureId = payload;
    }
  },
  updateLastPage(state, payload) {
    state.lastPage = payload;
  },
  updateLisaButtonTarget(state, payload: LisaButtonTarget) {
    if (state.user != null) {
      state.user.lisaButtonTarget = payload;
    }
  },
  updateStartUpBoard(state, payload: number | null) {
    if (state.user != null) {
      state.user.startUpBoardId = payload;
    }
  },
  updateModifier(state, payload: number) {
    if (state.user != null) {
      state.user.modifier = payload;
    }
  },
  update(state, payload) {
    if (payload.site && state.user != null) {
      state.user.site = payload.site;
      state.user.siteId = payload.site.id;
    }
    if (payload.startUpSiteId != null) {
      state.preferredSite = payload.startUpSiteId;
    } else if (state.preferredSite == null && state.user != null) {
      state.preferredSite = state.user.siteId;
    }
    if (payload.startUpBoardId != null) {
      state.preferredBoard = payload.startUpBoardId;
    }
  },
  updateSite(state, payload) {
    state.preferredSite = payload;
  },
  updateBoard(state, payload) {
    state.preferredBoard = payload;
  },
  setLanguage(state, language: string) {
    if (state.user != null) {
      state.user.language = language;
    }
  },
  setAccessRights(state, accessRights) {
    if (state.user != null) {
      state.user.accessRights = accessRights;
    }
  },
  set(state, user) {
    state.user = user;
    state.isLogin = true;
    if (user == null) {
      state.preferredSite = null;
      state.preferredBoard = null;
      state.isLogin = false;
      router.push("/");
      return;
    }
    if (user.startUpSiteId != null) {
      state.preferredSite = user.startUpSiteId;
    } else if (state.preferredSite == null && state.user != null) {
      state.preferredSite = state.user.siteId;
    }

    state.preferredBoard = user.startUpBoardId;
  },
};

export default {
  namespaced: true,
  state: localState,
  getters,
  actions,
  mutations,
};
